import { Component, NgZone, OnDestroy, OnInit, ChangeDetectorRef, Output, EventEmitter, Inject, ChangeDetectionStrategy, ErrorHandler } from '@angular/core';
import { BatteryStatus } from 'src/app/models/recordDto.model';
import { NodeRecord, SensorRecord } from 'src/app/models/sensorRecord.model';
import { ChartService } from 'src/app/services/chart.service';
import type * as am5 from '@amcharts/amcharts5';
import type * as am5xy from '@amcharts/amcharts5/xy';
import { ResizedEvent } from 'angular-resize-event-package';
import { ParameterLookup } from 'src/app/models/parameter.model';
import { UserPreferences } from 'src/app/models/userPreferences.model';
import { OnZoom } from 'src/app/components/chartBase/onZoom.interface';
import { SensorRecordService } from 'src/app/services/sensorRecord.service';
import { NodeSensorRecordsRequestModel } from 'src/app/models/nodeRecords.model';
import { AuthService } from 'src/app/services';
import { Subscription, forkJoin } from 'rxjs';
import { CommonService } from 'src/app/services/common.service';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { DateFilterTypeEnum } from 'src/app/enums/dateFilterType.enum';
import { Router } from '@angular/router';
import { ParameterConstants } from '../../shared/constants/parameter.constants';
import { ToastService } from 'src/app/services/toast.service';
import { LiveOption } from 'src/app/models/liveOption.model';
import { FLYOUT_DATA } from 'src/app/shared/flyout/flyout-container/flyout-data.token';
import { BatteryService } from 'src/app/services/battery.service';
import { TranslateService } from 'src/app/services/translate.service';

@Component({
  standalone: false,
  selector: 'app-node-details-popup',
  templateUrl: './node-details-popup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./node-details-popup.component.scss']
})
export class NodeDetailsPopupComponent implements OnInit, OnDestroy, OnZoom {
  @Output() hideNodeDetailEvent = new EventEmitter<boolean>();
  selectedNode: BatteryStatus;
  batteryInfo: { imgSrc: string; tooltip: string; percentage: string } = { imgSrc: '', tooltip: '', percentage: '' };
  nodeRecords: NodeRecord[] = [];
  chart: am5xy.XYChart;
  chartSlider: am5.DataItem<am5xy.IDateAxisDataItem>;
  initDisposer: am5.IDisposer;
  shouldRecordAnalyticsZoom = false;
  preferences: UserPreferences;
  shouldRecordVerticalZoom = false;
  disposer: am5.IDisposer;
  analyticsDisposer: am5.IDisposer;
  analyticsVerticalDisposer: am5.IDisposer;
  loadingDetails = false;
  isNodeDetailsEnabled = true;
  private resizeTimeoutId: ReturnType<typeof setTimeout> | null = null;
  subscriptions: Subscription[] = [];
  selectedOrg: OrganizationLevel;
  selectedParameter: ParameterLookup;
  selectedDateRange: any;
  chartHasNoData = false;
  liveOption: LiveOption;
  chartInitialized = false;

  CHART_DIV_ID = 'node-details-popup-chart';
  constructor(
    @Inject(FLYOUT_DATA) public data: string,
    private zone: NgZone,
    private authService: AuthService,
    private commonService: CommonService,
    private chartService: ChartService,
    private sensorRecordService: SensorRecordService,
    private dateFormatService: DateFormatService,
    private router: Router,
    private toastService: ToastService,
    private changeDetectorRef: ChangeDetectorRef,
    private batteryService: BatteryService,
    private translateService: TranslateService,
    private errorHandler: ErrorHandler
  ) {
    const currentUser = authService.getCurrentUser();
    this.preferences = currentUser.preferences;
    this.CHART_DIV_ID = this.generateUniqueChartId();
  }

  ngOnInit(): void {
    this.selectedParameter = this.commonService.selectParameterEmitter.value;
    this.selectedOrg = this.commonService.selectedOrgEmitter.value;
    this.selectedDateRange = this.commonService.selectedDateFilterEmitter.value;

    this.loadHeaderData();

    if (typeof this.data === 'string' && this.data) {
      this.openNodeDetails(this.data);
    }

    this.subscriptions.push(
      this.commonService.liveOptionSubject.subscribe(
        (res) => (this.liveOption = res)));

    this.subscriptions.push(
      this.authService.refreshTimezone.subscribe(async () => {
        try {
          const { am5: am5Ns } = await this.chartService.getAmCharts5();
          if (!this.chart || this.chart.root.isDisposed()) return;
          const timeZone = this.dateFormatService.getTimeZone();
          if (timeZone && timeZone !== this.chart.root.timezone?.name) {
            this.chart.root.timezone = am5Ns.Timezone.new(timeZone);
            this.chart.series.each((series) => series.data.setAll([...series.data.values]));
          }
        } catch (error) {
          this.errorHandler.handleError(error);
        }
      }));
  }


  private generateUniqueChartId(): string {
    const randomNum = Math.floor(Math.random() * 1000);
    return `node-details-popup-chart-${randomNum}`;
  }

  hideNodeDetailPopup() {
    if (this.selectedNode) {
      this.selectedNode = null;
      this.hideNodeDetailEvent.emit();
    }
  }

  showHideLoader(shouldShow: boolean) {
    this.loadingDetails = shouldShow;
    this.changeDetectorRef.detectChanges();
  }

  openNodeDetails(currNode: string) {
    if (!currNode) {
      this.toastService.displayErrorMessage('Error loading node details', true);
      return;
    }

    const payload: NodeSensorRecordsRequestModel = {
      orgLevelId: this.selectedOrg?.id,
      analyteId: this.selectedParameter?.id,
      endPointId: currNode,
      dateFilterType: this.selectedDateRange?.id ?? DateFilterTypeEnum.AllDates,
      startDate: this.selectedDateRange?.start ?? 0,
      endDate: this.selectedDateRange?.end ?? 0,
      includeVideo: false,
    };

    if (this.liveOption?.enabled) {
      const now = this.dateFormatService.convertStringToTimestamp(this.dateFormatService.formatDateTimeWithTimeZoneToString(new Date()));
      payload.dateFilterType = DateFilterTypeEnum.Custom;
      payload.endDate = now;
      const _8hoursInSeconds = 8 * 60 * 60;
      payload.startDate = now - _8hoursInSeconds;
    }

    this.showHideLoader(true);
    forkJoin({
      battery: this.sensorRecordService.getBatteryStatus(currNode),
      records: this.sensorRecordService.getNodeRecords(payload),
    }).subscribe({
      next: ({ battery, records }) => {
        this.selectedNode = battery;
        this.selectedNode.imgSrc = this.selectedNode?.batteryPercentage >= 70 ? 'green' :
          this.selectedNode?.batteryPercentage >= 35 ? 'yellow' : 'red';
        this.batteryInfo = this.getBatteryInfo();
        this.selectedNode.formattedDate = this.dateFormatService.formatTimestampToString(
          this.selectedNode.timestamp, this.dateFormatService.shortDateFormat);
        this.selectedNode.formattedTime = this.dateFormatService.formatTimestampToString(
          this.selectedNode.timestamp, this.dateFormatService.dateToTimeFormat);

        this.changeDetectorRef.detectChanges();

        const nodeRecords = records?.nodeRecords || [];
        this.isNodeDetailsEnabled = nodeRecords.length > 0;

        setTimeout(async () => {
          try {
            if (!this.chartInitialized) {
              await this.initializeChart();
              this.chartInitialized = true;
            }

            this.nodeRecords = nodeRecords;
            this.drawMaps();
          } catch (error) {
            this.toastService.showError(error);
            this.showHideLoader(false);
          }
        }, 0);
      },
      error: (error) => {
        this.toastService.showError(error);
        this.nodeRecords = [];
        this.showHideLoader(false);
      }
    });
  }


  drawMaps() {
    this.chartHasNoData = false;

    if (!this.chart) {
      this.chartHasNoData = true;
      this.showHideLoader(false);
      return;
    }

    const sensorRecords: NodeRecord[] = [];
    this.nodeRecords.forEach((node) => {
      const sensor: any = { ...node, deviceName: null };
      sensorRecords.push(sensor);
    });
    let parameterName: string = null;
    sensorRecords.forEach(
      (record) => {
        record.timestamp *= 1000;

        if (record?.value != null) {
          parameterName = this.selectedParameter.name;
          record[ParameterConstants.parameterFieldName] = record.value;
        }
      });

    this.shouldRecordAnalyticsZoom = false;
    this.shouldRecordVerticalZoom = false;
    this.zone.runOutsideAngular(async () => {
      if (sensorRecords.length > 0) {
        this.chartSlider = await this.chartService.createChartRange(this.chart);
        await this.chartService.drawSensorRecordChart(
          this.chart,
          sensorRecords,
          [this.selectedParameter.parameterType],
          null,
          [],
          null,
          null,
          null,
          'monitoring-dashboard',
          false,
          parameterName,
          ParameterConstants.parameterFieldName,
        );
        this.createChartZoomEvent();
      } else {
        this.chartHasNoData = true;
      }
      this.chartService.disposeEvent(this.initDisposer);
    });
    this.showHideLoader(false);
  }

  createChartZoomEvent() {
    this.chartService.disposeEvent(this.disposer);
    this.disposer = this.zone.runOutsideAngular(() => {
      return this.chartService.createChartZoomEvent(this.chart, this);
    });
    this.chartService.disposeEvent(this.analyticsDisposer);
    this.analyticsDisposer = this.zone.runOutsideAngular(() => {
      return this.chartService.createChartAnalyticsEvent(this.chart, this);
    });
    this.chartService.disposeEvent(this.analyticsVerticalDisposer);
    this.analyticsVerticalDisposer = this.zone.runOutsideAngular(() => {
      return this.chartService.createChartAnalyticsVerticalZoomEvent(
        this.chart,
        this
      );
    });
  }
  onResized(event: ResizedEvent) {
    if (this.resizeTimeoutId !== null) {
      clearTimeout(this.resizeTimeoutId);
    }
    this.resizeTimeoutId = setTimeout(() => {
      this.resizeTimeoutId = null;
      this.chartService.onResized(this.chart, event, this.CHART_DIV_ID);
    }, 200);
  }

  async initializeChart(): Promise<void> {
    this.chart = await this.zone.runOutsideAngular(
      () => this.chartService.createChart(this.CHART_DIV_ID)
    );
    this.initDisposer = this.zone.runOutsideAngular(() =>
      this.chartService.createChartInitialZoomEvent(this.chart, this, false)
    );
  }


  onZoom(sensorRecords: SensorRecord[]) {
    if (
      sensorRecords &&
      sensorRecords.length > 0 &&
      this.initDisposer &&
      !this.initDisposer.isDisposed()
    ) {
      this.initDisposer.dispose();
    }
  }

  onInitialZoom(sensorRecords: SensorRecord[]) {
    if (
      this.initDisposer &&
      !this.initDisposer.isDisposed() &&
      sensorRecords &&
      sensorRecords.length > 0
    ) {
    }
  }

  onAnalyticsHorizontalZoom() {
    if (!this.shouldRecordAnalyticsZoom) {
      this.shouldRecordAnalyticsZoom = true;
      return;
    }
  }

  onAnalyticsVerticalZoom() {
    if (!this.shouldRecordVerticalZoom) {
      this.shouldRecordVerticalZoom = true;
      return;
    }
  }

  goToNodeDetails() {
    this.router.navigate([
      '/dashboard/node-detail/',
      this.selectedNode?.endPointId,
    ]);
  }

  setSelectedOrg(selectedOrg: OrganizationLevel) {
    this.selectedOrg = selectedOrg;
  }

  setSelectedParameter(selectedParameter: ParameterLookup) {
    this.selectedParameter = selectedParameter;
  }

  setSelectedDateRange(selectedDateRange: any) {
    this.selectedDateRange = selectedDateRange;
  }

  loadHeaderData() {
    this.subscriptions.push(
      this.commonService.selectParameterEmitter.subscribe((parameter) => {
        if (parameter && parameter !== this.selectedParameter) {
          this.setSelectedParameter(parameter);
        }
      })
    );
    this.subscriptions.push(
      this.commonService.selectedOrgEmitter.subscribe((selectedOrg) => {
        if (selectedOrg && (!this.selectedOrg || this.selectedOrg.id !== selectedOrg.id)) {
          this.setSelectedOrg(selectedOrg);
        }
      })
    );
    this.subscriptions.push(
      this.commonService.selectedDateFilterEmitter.subscribe((dateFilter) => {
        if (dateFilter && dateFilter !== this.selectedDateRange) {
          this.setSelectedDateRange(dateFilter);
        }
      })
    );
  }

  ngOnDestroy() {
    if (this.resizeTimeoutId !== null) clearTimeout(this.resizeTimeoutId);
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    if (this.chart) this.chart.root.dispose();
    this.chartService.disposeEvent(this.disposer);
  }

  getBatteryInfo(): { imgSrc: string, tooltip: string, percentage: string } {
    const batteryPercentage = this.selectedNode?.batteryPercentage ?? null;
    const batteryImgSrcs = this.batteryService.getBatteryImgSrcsCommon(batteryPercentage, false);
    const batteryTooltips = this.batteryService.getBatteryTooltipsCommon(batteryPercentage, false);

    return {
      imgSrc: batteryImgSrcs.length > 0 ? batteryImgSrcs[0] : '',
      tooltip: batteryTooltips.length > 0 ? this.translateService.getDynamicTranslation(batteryTooltips[0]) : '',
      percentage: batteryPercentage !== null ? `${batteryPercentage}%` : ''
    };
  }

  getSignalStrengthTitle(): string {
    const signal = this.selectedNode?.signalStrength;
    const key = signal != null ? 'Signal Strength' : 'No signal strength information available';
    const params = signal != null ? { value: signal } : {};
    return this.translateService.getDynamicTranslation(key, params);
  }
}
